import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Send, RotateCcw } from "lucide-react";
import { Panel } from "@/components/croc/ui";
import { fmtNum } from "@/lib/format";
import { CHAIN } from "@/contracts/croc";
import api from "@/lib/api";

const TONE = { sent: "emerald-text", pending: "text-white", sending: "text-white", failed: "text-red-400", stuck: "gold-text" };

export const TokenMirrorPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.adminTokenMirror(token).then(setData).catch(() => {}), [token]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const retry = async () => {
    try {
      const r = await api.adminTokenMirrorRetry(token);
      toast.success(`${r.requeued} failed transfer(s) re-queued`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
  };
  if (!data) return null;
  const s = data.sender || {};
  const c = data.counts || {};
  return (
    <Panel className="mb-6" data-testid="admin-token-mirror-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2"><Send className="w-4 h-4 emerald-text" /><span className="font-display font-bold text-white">Stake Token Transfers</span></div>
        <button onClick={retry} data-testid="admin-token-mirror-retry-btn"
          className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white hover:border-[#1CFD4C]/50 transition-colors">
          <RotateCcw className="w-3.5 h-3.5" /> Retry failed
        </button>
      </div>
      {!s.enabled ? (
        <p className="text-xs text-red-400" data-testid="admin-token-mirror-disabled">Sender not configured — add TOKEN_SENDER_PRIVATE_KEY to backend .env and restart.</p>
      ) : (
        <div className="grid sm:grid-cols-3 gap-2 text-xs mb-3" data-testid="admin-token-mirror-sender">
          <div className="rounded-lg bg-[#090E0B] border border-white/10 px-3 py-2"><div className="text-muted-foreground">Sender</div><div className="font-num text-white break-all">{s.sender}</div></div>
          <div className="rounded-lg bg-[#090E0B] border border-white/10 px-3 py-2"><div className="text-muted-foreground">Token balance</div><div className="font-num text-white">{fmtNum(s.token_balance ?? 0, 2)}</div></div>
          <div className="rounded-lg bg-[#090E0B] border border-white/10 px-3 py-2"><div className="text-muted-foreground">BNB (gas)</div><div className="font-num text-white">{fmtNum(s.bnb_balance ?? 0, 5)}</div></div>
        </div>
      )}
      <div className="flex flex-wrap gap-4 text-xs mb-3" data-testid="admin-token-mirror-counts">
        {["sent", "pending", "failed", "stuck"].map((k) => (
          <span key={k} className={TONE[k]}>{k}: {c[k]?.n ?? 0} ({fmtNum(c[k]?.amount ?? 0, 2)})</span>
        ))}
      </div>
      <div className="space-y-1 max-h-56 overflow-auto">
        {(data.recent || []).map((j) => (
          <div key={j.id} className="flex items-center justify-between gap-2 text-[11px] font-num border-b border-white/5 py-1">
            <span className="text-muted-foreground">{j.created_at?.slice(0, 16).replace("T", " ")}</span>
            <span className="text-white">{j.address?.slice(0, 8)}… {j.tier_key}</span>
            <span className="text-white">{fmtNum(j.amount, 4)}</span>
            {j.tx_hash
              ? <a href={`${CHAIN.explorer}/tx/${j.tx_hash}`} target="_blank" rel="noreferrer" className={TONE[j.status]}>{j.status} ↗</a>
              : <span className={TONE[j.status]} title={j.error || ""}>{j.status}</span>}
          </div>
        ))}
      </div>
    </Panel>
  );
};
